/**
 * Navigation dataLayer tracking.
 *
 * Pushes `dl_nav_interaction` for every navigation click so GTM can report on
 * which tier and branch of the menu drives traffic. Payload:
 *   { event, nav_location, nav_action, nav_tier, nav_parent_1,
 *     nav_breadcrumb, nav_text, link_url }
 */
(() => {
  'use strict';

  const EVENT_NAME = 'dl_nav_interaction';
  const MOBILE_BREAKPOINT = 1024;

  // First match wins, so more specific targets come first.
  const TARGETS = [
    { selector: '.nav-promo__link', tier: 1, action: 'image_click' },
    { selector: '.menu__item--tertiary > a', tier: 2, action: 'link_click' },
    { selector: 'a.submenu__heading', tier: 1, action: 'link_click' },
    { selector: '.menu__item--primary .dropdown-item > a', tier: 0, action: 'link_click' },
    { selector: '.secondary-nav__dropdown a', tier: 1, action: 'link_click' },
    { selector: '.secondary-nav__item > a', tier: 0, action: 'link_click' },
    { selector: '.navigation-account-contact a', tier: 0, action: 'link_click' },
    { selector: '.nav-recently-viewed__item a', tier: 0, action: 'image_click' },
  ];

  const isMobile = () => window.innerWidth < MOBILE_BREAKPOINT;

  // textContent minus <noscript> subtrees: the responsive-img snippet's
  // noscript fallback otherwise surfaces as a serialized <img> tag.
  const text = (el) => {
    if (!el) return '';
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => (node.parentElement && node.parentElement.closest('noscript')
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT),
    });

    let out = '';
    while (walker.nextNode()) out += walker.currentNode.nodeValue;
    return out.replace(/\s+/g, ' ').trim();
  };

  // Image tiles: caption, else link text, else the image's alt.
  const labelFor = (el, config) => {
    if (config.action !== 'image_click') return text(el);
    const caption = el.querySelector('.nav-promo__title');
    if (caption) return text(caption);
    const img = el.querySelector('img');
    return text(el) || (img && img.getAttribute('alt')) || '';
  };

  const linkUrl = (el) => {
    const raw = el.getAttribute('href');
    if (!raw || raw.charAt(0) === '#') return '';
    try {
      const url = new URL(el.href, window.location.origin);
      return url.origin === window.location.origin ? url.pathname + url.search : url.href;
    } catch (e) {
      return raw;
    }
  };

  const matchTarget = (start) => {
    for (const config of TARGETS) {
      const el = start.closest(config.selector);
      if (el) return { el, config };
    }
    return null;
  };

  // Title of the top-level item the click happened under.
  const primaryTitle = (el) => {
    const primary = el.closest('.menu__item--primary');
    if (primary) return text(primary.querySelector('.dropdown-item > a'));
    const secondary = el.closest('.secondary-nav__item');
    return secondary ? text(secondary.querySelector(':scope > a')) : '';
  };

  const columnTitle = (el) => {
    const column = el.closest('.menu__item--secondary');
    return column ? text(column.querySelector('.submenu__heading')) : '';
  };

  // Primary > column > label, with empty and duplicate segments dropped.
  const breadcrumb = (el, label) => {
    const segments = [primaryTitle(el), columnTitle(el), label];
    return segments
      .filter((segment, index) => segment && segments.indexOf(segment) === index)
      .join(' > ');
  };

  const locationFor = (el) => {
    if (el.closest('.secondary-nav')) return 'header_secondary';
    if (isMobile() && document.body.classList.contains('mmenu__open')) return 'mobile_menu';
    return 'header';
  };

  // On mobile, primary items with panels and mega section headings toggle
  // instead of navigating, so those clicks report as expands.
  const actionFor = (el, config) => {
    if (config.action !== 'link_click' || !isMobile()) return config.action;
    if (config.tier === 0
      && el.closest('.menu--item--has-dropdown')
      && el.getAttribute('aria-expanded') === 'false') return 'expand';
    if (config.tier === 1 && el.matches('.mega__menu .submenu__heading')) return 'expand';
    return config.action;
  };

  // Capture phase: theme handlers stopPropagation on some menu clicks.
  document.addEventListener('click', (event) => {
    if (!event.target.closest || !event.target.closest('.navigation, .secondary-nav')) return;

    const match = matchTarget(event.target);
    if (!match) return;

    const { el, config } = match;
    const label = labelFor(el, config);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: EVENT_NAME,
      nav_location: locationFor(el),
      nav_action: actionFor(el, config),
      nav_tier: config.tier,
      nav_parent_1: primaryTitle(el),
      nav_breadcrumb: breadcrumb(el, label),
      nav_text: label,
      link_url: linkUrl(el),
    });
  }, true);
})();
